'use client';

import { isToday } from 'date-fns/isToday';
import { useEffect, useState } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  formatMatchdayStartDatetime,
  formatMatchdayStartDatetimeShort,
} from '@/utils';
import { readFragment, type FragmentOf } from '@/utils/graphql';
import { matchdayCardDateFragment } from './matchdayCardDateFragment';

export function MatchdayCardDate({
  matchday: _matchday,
}: {
  matchday: FragmentOf<typeof matchdayCardDateFragment>;
}) {
  const matchday = readFragment(matchdayCardDateFragment, _matchday);

  const [matchdayIsToday, setMatchdayIsToday] = useState<boolean>();
  const [localDatetime, setLocalDatetime] = useState<{
    day: string;
    month: string;
    time: string;
  } | null>();

  useEffect(() => {
    if (!matchday.start_datetime) {
      return;
    }

    const _matchdayIsToday = isToday(matchday.start_datetime);
    setMatchdayIsToday(_matchdayIsToday);

    const startDatetime = formatMatchdayStartDatetimeShort(
      matchday.start_datetime,
    );
    setLocalDatetime(startDatetime);
  }, [matchday.start_datetime]);

  if (matchday.postponed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="text-sm uppercase">Apl</div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Aplazado</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (!localDatetime) {
    return null;
  }

  if (matchdayIsToday) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-col items-center text-red-600">
              <div className="font-semibold">{localDatetime.time}</div>
              <div className="text-sm uppercase">Hoy</div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>{formatMatchdayStartDatetime(matchday.start_datetime!)}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col items-center">
            <div className="text-xl">{localDatetime.day}</div>
            <div className="text-xs uppercase">{localDatetime.month}</div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <span>{formatMatchdayStartDatetime(matchday.start_datetime!)}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
